import {
  Button,
  DialogActions,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
  Chip,
  Box,
  Alert,
  Card,
  CardContent,
  TextField,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import DialogHeader from "../../../components/Dialog/DialogHeader";
import UserDialog from "../../../components/Dialog/UserDialog";
import { Api } from "../../../request/api/api";
import { Advancement, IncreaseLevel } from "../../../model/Dto/Advancement";

interface State {
  selectedAdvancements: string[];
  totalCost: number;
  experience: number;
}

export interface LevelUpDialogProps {
  fighterId: string;
  open: boolean;
  fetchData: () => void;
  onClose: () => void;
}

export default function LevelUpDialog({
  fighterId,
  onClose,
  fetchData,
  open,
}: LevelUpDialogProps) {
  const [state, setState] = useState<State>({
    selectedAdvancements: [],
    totalCost: 0,
    experience: 0,
  });
  
  const [levelUpInfo, setLevelUpInfo] = useState<IncreaseLevel | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Загрузка информации для повышения уровня
  useEffect(() => {
    if (open) {
      setLoading(true);
      setState({ 
        selectedAdvancements: [], 
        totalCost: 0, 
        experience: 0 
      });
      setError(null);
      
      Api.fighter.getIncreaseLevel(fighterId)
        .then((result) => {
          if (result.success && result.increaseLevel) {
            setLevelUpInfo(result.increaseLevel);
            setState(prev => ({
              ...prev,
              experience: result.increaseLevel!.expRequired
            }));
          } else {
            setError("Failed to load level up information");
          }
          setLoading(false);
        })
        .catch((error) => {
          console.error("Error loading level up info:", error);
          setError("Failed to load level up information");
          setLoading(false);
        });
    }
  }, [open, fighterId]);

  // Обработчик изменения выбора адвансов
  const handleAdvancementChange = (event: SelectChangeEvent<string[]>) => {
    const selectedIds = event.target.value as string[];
    
    // Ограничиваем выбор максимум 2 адвансами
    if (selectedIds.length > 2) {
      setError("Можно выбрать максимум 2 прокачки");
      return;
    }
    
    setError(null);
    
    // Рассчитываем общую стоимость
    const totalCost = selectedIds.reduce((sum, id) => {
      const advancement = levelUpInfo?.advancements.find(a => a.id === id);
      return sum + (advancement?.cost || 0);
    }, 0);
    
    setState({
      ...state,
      selectedAdvancements: selectedIds,
      totalCost,
    });
  };

  // Обработчик изменения опыта
  const handleExperienceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value) || 0;
    setState({
      ...state,
      experience: value,
    });
  };

  // Удаление выбранного адванса
  const removeAdvancement = (advancementId: string) => {
    const newSelection = state.selectedAdvancements.filter(id => id !== advancementId);
    const newTotalCost = newSelection.reduce((sum, id) => {
      const adv = levelUpInfo?.advancements.find(a => a.id === id);
      return sum + (adv?.cost || 0);
    }, 0);
    
    setState({
      ...state,
      selectedAdvancements: newSelection,
      totalCost: newTotalCost,
    });
  };

  // Проверка валидности формы
  const isFormValid = (): boolean => {
    if (!levelUpInfo) return false;
    
    const hasValidAdvancements = state.selectedAdvancements.length >= 1 && 
                                state.selectedAdvancements.length <= 2;
    
    const hasValidExperience = state.experience <= levelUpInfo.currentFighterExp;
    
    return hasValidAdvancements && hasValidExperience;
  };

  // Получение текста ошибки для поля опыта
  const getExperienceError = (): string | null => {
    if (!levelUpInfo) return null;
    
    if (state.experience > levelUpInfo.currentFighterExp) {
      return "У бойца недостаточно опыта";
    }
    
    return null;
  };

  // Обработчик сохранения
  const handleSave = async () => {
    if (!isFormValid()) {
      const experienceError = getExperienceError();
      if (experienceError) {
        setError(experienceError);
      } else if (state.selectedAdvancements.length === 0) {
        setError("Выберите хотя бы одну прокачку");
      } else if (state.selectedAdvancements.length > 2) {
        setError("Можно выбрать не более двух прокачек");
      } else {
        setError("Поля заполнены некорректно");
      }
      return;
    }
    
    setLoading(true);
    try {
      const result = await Api.fighter.increaseLevel(
        fighterId,
        state.selectedAdvancements,
        state.experience
      );
      
      if (result) {
        setSuccess(true);
        setTimeout(() => {
          onClose();
          fetchData();
        }, 1000);
      } else {
        setError("Ошибка при повышении уровня");
      }
    } catch (error) {
      console.error("Ошибка при повышении уровня:", error);
      setError("Ошибка при повышении уровня");
    } finally {
      setLoading(false);
    }
  };

  // Получение выбранных адвансов с полной информацией
  const getSelectedAdvancements = (): Advancement[] => {
    return state.selectedAdvancements
      .map(id => levelUpInfo?.advancements.find(a => a.id === id))
      .filter((adv): adv is Advancement => adv !== undefined);
  };

  const handleClose = () => {
    setState({ 
      selectedAdvancements: [], 
      totalCost: 0, 
      experience: 0 
    });
    setLevelUpInfo(null);
    setError(null);
    setSuccess(false);
    onClose();
  };

  const selectedAdvancements = getSelectedAdvancements();
  const experienceError = getExperienceError();

  return (
    <UserDialog handleClose={handleClose} open={open}>
      <DialogHeader
        loading={loading}
        title="Повышение уровня бойца"
      />
      <DialogContent sx={{ minHeight: "250px", minWidth: {xs: "300px", lg: "400px"} }}>
        <Stack spacing={3}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success">
              Повышение уровня выполнено!
            </Alert>
          )}

          {/* Информация о текущем опыте */}
          {levelUpInfo && (
            <Card variant="outlined">
              <CardContent>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      Текущее количество опыта:
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {levelUpInfo.currentFighterExp}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      Требуемое количество опыта:
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {levelUpInfo.expRequired}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          )}

          {/* Поле ввода опыта */}
          {levelUpInfo && (
            <TextField
              label="Стоимость в очках опыта (XP)"
              type="number"
              value={state.experience}
              onChange={handleExperienceChange}
              error={!!experienceError}
              helperText={experienceError || `Введите количество XP, которые будут потрачены`}
              variant="outlined"
              fullWidth
              InputProps={{
                inputProps: { 
                  min: levelUpInfo.expRequired,
                  max: levelUpInfo.currentFighterExp
                }
              }}
            />
          )}

          {/* Выбор адвансов */}
          <FormControl variant="filled" fullWidth>
            <InputLabel>Повышения (выберите 1-2)</InputLabel>
            <Select
              multiple
              value={state.selectedAdvancements}
              onChange={handleAdvancementChange}
              label="Повышения (выберите 1-2)"
              disabled={!levelUpInfo}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.length === 0 ? (
                    <Typography color="text.secondary">
                      Выберите повышения...
                    </Typography>
                  ) : (
                    selected.map((value) => {
                      const advancement = levelUpInfo?.advancements.find(a => a.id === value);
                      return (
                        <Chip 
                          key={value} 
                          label={advancement?.name || value}
                          size="small"
                          onDelete={() => removeAdvancement(value)}
                          onMouseDown={(event) => {
                            event.stopPropagation();
                          }}
                        />
                      );
                    })
                  )}
                </Box>
              )}
            >
              {levelUpInfo?.advancements.map((advancement) => (
                <MenuItem 
                  key={advancement.id} 
                  value={advancement.id}
                  disabled={state.selectedAdvancements.length >= 2 && !state.selectedAdvancements.includes(advancement.id)}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <Typography>{advancement.name}</Typography>
                    <Typography color="text.secondary" sx={{ ml: 2 }}>
                      {advancement.cost} cr.
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Панель общей стоимости */}
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Изменение рейтинга:
                </Typography>
                <Chip 
                  label={`${state.totalCost} cr.`}
                  color={state.totalCost > 0 ? "primary" : "default"}
                  variant={state.totalCost > 0 ? "filled" : "outlined"}
                  sx={{ 
                    fontWeight: 'bold', 
                    fontSize: '1rem',
                    minWidth: '100px'
                  }}
                />
              </Box>
              
              {/* Мини-отображение выбранных адвансов под общей стоимостью */}
              {selectedAdvancements.length > 0 && (
                <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selectedAdvancements.map((advancement) => (
                    <Chip
                      key={advancement.id}
                      label={`${advancement.name} (${advancement.cost} cr.)`}
                      size="small"
                      variant="outlined"
                      onDelete={() => removeAdvancement(advancement.id)}
                    />
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>

          <Typography variant="caption" color="text.secondary">
            Выберите от одного до двух повышений. Стоймость будет подсчитана автоматически.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Закрыть
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          color="primary"
          disabled={loading || !isFormValid()}
        >
          {loading ? "Сохранение..." : "Повысить уровень"}
        </Button>
      </DialogActions>
    </UserDialog>
  );
}